/* eslint-disable max-params */
/* eslint-disable max-lines */
// internal-deps
import {
    BOOLEAN_AS_STRING,
    DELIVERY_SPEED,
    DELIVERY_SPEED_STRATEGY,
    DeliverySpeedDetail,
    DeliverySpeedOption,
    DeliverySpeedPresentationInfo,
    DeliverySpeedPriceInfo,
    ReviewedCartApiResponse,
    Option,
    NativeLocalCart,
    ServiceabilityParams,
    DeliverySpeedOptionOfferInfo,
    IStressComms,
} from "@swiggy-private/cart-types";
import { GlobalHeaderState, SwgyHeaderKeysList, getBridgeSdk } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";
import { computeShouldRenderDeliveryOptionBanner } from "@swiggy-private/cart-ui";
import { STORAGE_TYPES } from "@swiggy-private/native-bridge";

// constants
import {
    APP_STORAGE_KEYS_FOR_DELIVERY_SPEED,
    CROUTON_LIMIT,
    DELIVERY_TYPE_NOT_AVAILABLE,
    DELIVERY_TYPE_ADD_ADDRESS,
    SINGLE_MODE_SERVICABLE_SLA_TEXT,
} from "@cart-client/constants/CartDeliverySpeed";
import { CART_STATUS_CODES } from "@swiggy-private/cart-ui";
import { getStressComms } from "./StressCommsHelper";

export interface DeliverySpeedAppStorage {
    [APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.SID]: string;
    [APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT]: number;
}

export const getDeliverySpeedPricingInfo = (
    cartResponse?: ReviewedCartApiResponse | null,
): DeliverySpeedPriceInfo | undefined => {
    return cartResponse?.delivery_speed_pricing_info;
};

export const getDeliverySpeedPresentationInfo = (
    cartResponse?: ReviewedCartApiResponse | null,
): DeliverySpeedPresentationInfo | undefined => {
    return cartResponse?.presentation_attributes_keys?.delivery_speed_presentation_info;
};

export const getOptionsWithUpdatedHighlights = (
    deliverySpeedOptions: DeliverySpeedOption[],
    cartResponse?: ReviewedCartApiResponse | null,
): DeliverySpeedOption[] => {
    const deliveryPricingInfo = getDeliverySpeedPricingInfo(cartResponse);
    const norushDiscountMessage = deliveryPricingInfo?.no_rush_discount_message;

    if (isEmpty(norushDiscountMessage)) {
        return deliverySpeedOptions;
    }
    return deliverySpeedOptions.map((option: DeliverySpeedOption) => {
        if (
            option.delivery_speed.toUpperCase() === DELIVERY_SPEED.BATCH_NO_RUSH &&
            !option.highlight_bullet_points.includes(norushDiscountMessage)
        ) {
            option.highlight_bullet_points.unshift(norushDiscountMessage);
        }
        return option;
    });
};

/**
 * Get offer information including time left, color, and message.
 * @param {DeliverySpeedOptionOfferInfo} offerInfo - The offer information.
 * @returns {{ timeLeftInSeconds: number | undefined, color: string, offerMessage: string }} The offer details.
 */
export const getOfferInfo = (offerInfo?: DeliverySpeedOptionOfferInfo) => {
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { offer_timer_expiry, offer_timer_start, color, offer_message, offer_pre_timer_message, gradient } =
        offerInfo || {};

    let timeLeftInSeconds = undefined;
    let offerMessage = offer_message;

    // incase currentTime is greather than start and smaller than expiry.
    if (
        !isEmpty(offer_timer_start) &&
        !isEmpty(offer_timer_expiry) &&
        currentTime > Number(offer_timer_start) &&
        currentTime < Number(offer_timer_expiry)
    ) {
        timeLeftInSeconds = Number(offer_timer_expiry) - currentTime;
    } else if (!isEmpty(offer_timer_expiry) && currentTime < Number(offer_timer_expiry)) {
        // incase current time is before the expiry time then show offer_pre_timer_message
        offerMessage = offer_pre_timer_message;
    } else {
        // incase current time is beyond the expiry time then show offer_pre_timer_message
        offerMessage = "";
    }

    return {
        timeLeftInSeconds,
        color,
        offerMessage,
        gradient,
    };
};

export const getDeliverySpeedWidgetInfoV2 = (
    cartResponse?: ReviewedCartApiResponse | null,
    localCart?: NativeLocalCart | null,
    isAddressServiceable?: boolean,
    isRestaurantUnservicable?: boolean,
    updateCartInProgress?: boolean,
): {
    options: Option[];
    optInText?: string;
    slaComms?: IStressComms;
} | null => {
    const deliverySpeedPresentationInfo = getDeliverySpeedPresentationInfo(cartResponse);
    const deliverySpeedWidgetInfo = deliverySpeedPresentationInfo?.delivery_speed_widget_info ?? null;

    if (isEmpty(deliverySpeedWidgetInfo)) return null;

    let optionsV2: Option[] = [];
    const options = deliverySpeedWidgetInfo.delivery_speed_options;
    let updatedOptionsForDeliveryType = deliverySpeedWidgetInfo.delivery_speed_options; // created duplicate of options for abpve
    deliverySpeedWidgetInfo.delivery_speed_options = getOptionsWithUpdatedHighlights(options, cartResponse);

    const { selectedAddress } = localCart || {};
    const { sla_options: slaOptions } = selectedAddress || [];

    const enabledOptions: number[] = [];

    //Todo: For hotfix express not to be shown if not available in sla options (Hacky fix)
    const isExpressOptionServiceable = slaOptions?.filter(
        (option: DeliverySpeedDetail) => option?.type === DELIVERY_SPEED.EXPRESS,
    );

    // if express is not serviceable dont show as disabled option for end user
    if (isEmpty(isExpressOptionServiceable) || isExpressOptionServiceable?.length === 0) {
        updatedOptionsForDeliveryType = updatedOptionsForDeliveryType.filter(
            (presentationOption: DeliverySpeedOption) => presentationOption.delivery_speed != DELIVERY_SPEED.EXPRESS,
        );
    }
    // -- hacky logic ends

    updatedOptionsForDeliveryType?.forEach((option: DeliverySpeedOption, index) => {
        let isDisabled = false;

        const slaData = (slaOptions as DeliverySpeedDetail[])?.find(
            (slaOption) => slaOption?.type === option?.delivery_speed,
        );

        if (
            isEmpty(slaOptions) ||
            isEmpty(slaData) ||
            slaData?.strategy?.toUpperCase() !== DELIVERY_SPEED_STRATEGY.VISIBLE
        ) {
            isDisabled = true;
        }

        if (!isDisabled) {
            enabledOptions.push(index);
        }

        let v2SubHeading = option?.bullet_points?.[0];

        if (option.delivery_speed === DELIVERY_SPEED.EXPRESS) {
            if (isDisabled && isEmpty(v2SubHeading)) {
                v2SubHeading = DELIVERY_TYPE_NOT_AVAILABLE;
            }
        } else if (isDisabled) {
            if (!isRestaurantUnservicable && !isAddressServiceable) {
                v2SubHeading = DELIVERY_TYPE_ADD_ADDRESS;
            } else {
                v2SubHeading = DELIVERY_TYPE_NOT_AVAILABLE;
            }
        }

        return optionsV2.push({
            heading: option.heading_text,
            subHeading: v2SubHeading,
            headingInfo: isDisabled ? undefined : option?.highlight_bullet_points?.[0],
            meta: {
                showShimmer: option.highlight_bullet_points_meta?.[0]?.shimmer === BOOLEAN_AS_STRING.TRUE,
                tooltipData: {
                    title: option.highlight_bullet_points_meta?.[0]?.title,
                    description: option.highlight_bullet_points_meta?.[0]?.description,
                },
                highlightColor: option.highlight_colour,
                headingInfoColor: option?.highlight_bullet_points_meta?.[0]?.highlight_color,
            },
            slaData: isDisabled ? undefined : slaData,
            option: option.delivery_speed,
            isDisabled,
            bannerData: {
                text: option.bottom_banner,
                color: option.bottom_meta?.banner_color,
                bgColor: option.bottom_meta?.banner_bg_color,
                iconId: option.bottom_meta?.icon_id,
                ctaEnabled: option.bottom_meta?.cta_info?.cta_enabled === "true",
            },

            offerInfo: !updateCartInProgress ? getOfferInfo(option.offer_info) : undefined,

            // render a standalone complete banner styled component to give better delivery mode recall
            deliverySpeedOnlyPresentation: option?.delivery_speed_only_presentation || "",
            deliverySpeedOnlyPresentationIcon: option?.delivery_speed_only_presentation_icon || "",
            // render an icon instead of delivery mode text in the select delivery speed option
            deliverySpeedWithIcon: option?.delivery_speed_with_icon || "",
        });
    });

    // This is to cater to the case where only one sla is serviceable
    // and we have to show a disabled text at the very end of list
    if (enabledOptions.length === 1) {
        // if the enabled option is a banner presentation, then we do not need to show the disabled text at the very end of list
        const isEnabledOptionUIBannerPresentation = computeShouldRenderDeliveryOptionBanner(
            optionsV2[enabledOptions[0]],
        );

        const isPreorder = optionsV2[enabledOptions[0]].slaData?.type === DELIVERY_SPEED.PREORDER;
        if (isEnabledOptionUIBannerPresentation || isPreorder) {
            optionsV2 = [optionsV2[enabledOptions[0]]];
        } else {
            optionsV2 = [
                optionsV2[enabledOptions[0]],
                {
                    heading: "",
                    subHeading:
                        deliverySpeedWidgetInfo.single_mode_servicable_sla_text || SINGLE_MODE_SERVICABLE_SLA_TEXT,
                    option: "",
                    isDisabled: true,
                },
            ];
        }
    }

    let slaComms = getStressComms(localCart);
    const isAnyDeliveryOptionBannerBased = optionsV2.some((option) => computeShouldRenderDeliveryOptionBanner(option));

    // if any of the delivery option is banner based, then we do not need to show the sla logo
    // as slaLogo fallback is defined at portal we need to manage this disable behaviour on portal directly
    if (isAnyDeliveryOptionBannerBased && !isEmpty(slaComms?.slaText)) {
        slaComms = { ...slaComms, slaLogo: "" };
    }

    return {
        options: optionsV2,
        optInText: deliverySpeedWidgetInfo.optin_text,
        slaComms,
    };
};

export const isDeliveryTypeEnabled = (slaOptions: DeliverySpeedDetail[], deliveryType: DELIVERY_SPEED): boolean => {
    return slaOptions?.some(
        (option) =>
            option?.type === deliveryType && option?.strategy?.toUpperCase() === DELIVERY_SPEED_STRATEGY.VISIBLE,
    );
};

export const hasValidDeliverySpeedOptions = (slaOptions?: DeliverySpeedDetail[]): boolean => {
    if (!isEmpty(slaOptions)) {
        const slaOptionsArr = slaOptions?.filter(
            (item: DeliverySpeedDetail) =>
                (item.type === DELIVERY_SPEED.STANDARD || item.type === DELIVERY_SPEED.BATCH_NO_RUSH) &&
                item.strategy != null &&
                item.strategy !== DELIVERY_SPEED_STRATEGY.NOT_VISIBLE,
        );
        return slaOptionsArr && slaOptionsArr.length > 0;
    }
    return false;
};

export const isSameSession = (storageData: DeliverySpeedAppStorage) =>
    storageData[APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.SID] === GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid);

export const isDeliverySpeedStorageValueValid = (
    storageKey: APP_STORAGE_KEYS_FOR_DELIVERY_SPEED,
    prevData: DeliverySpeedAppStorage,
): boolean => {
    const currentSid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid);

    const prevDeliverySpeedSid = prevData[APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.SID];

    if (storageKey === APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT) {
        const prevCroutonCount = prevData[APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT];

        return prevDeliverySpeedSid !== currentSid && prevCroutonCount < CROUTON_LIMIT;
    }
    return false;
};

export const getDeliverySpeedStorageValues = async (): Promise<DeliverySpeedAppStorage | null> => {
    try {
        const response = await getBridgeSdk().storage({
            type: STORAGE_TYPES.GET,
            data: {
                [APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.SID]: "",
                [APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT]: "",
            },
        });

        return {
            [APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.SID]: response?.data?.[APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.SID] ?? "",
            [APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT]: Number(
                response?.data?.[APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT] ?? 0,
            ),
        };
    } catch (e) {
        return Promise.resolve(null);
    }
};

export const setDeliverySpeedStorageValues = async (
    storageKey: APP_STORAGE_KEYS_FOR_DELIVERY_SPEED,
    prevData: DeliverySpeedAppStorage,
): Promise<void> => {
    const data: { [key: string]: string } = {};

    if (
        storageKey === APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT &&
        isDeliverySpeedStorageValueValid(APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT, prevData)
    ) {
        const prevCount = prevData[APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT] ?? 0;
        const newCroutonCount = prevCount + 1;

        data[APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT] = String(newCroutonCount);
        data[APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.SID] = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid);
    }

    if (isEmpty(data)) {
        return;
    }

    await getBridgeSdk().storage({
        type: STORAGE_TYPES.SAVE,
        data,
    });
};

export const resetCroutonCountStorageValue = async (): Promise<void> => {
    const data: { [key: string]: string } = {};

    data[APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT] = "0";
    data[APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.SID] = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid);

    await getBridgeSdk().storage({
        type: STORAGE_TYPES.SAVE,
        data,
    });
};

export const areBothStandardAndNorushEnabled = (slaOptions: DeliverySpeedDetail[]): boolean => {
    const isStandardDeliveryEnabled = isDeliveryTypeEnabled(slaOptions, DELIVERY_SPEED.STANDARD);
    const isNoRushDeliveryEnabled = isDeliveryTypeEnabled(slaOptions, DELIVERY_SPEED.BATCH_NO_RUSH);

    return isStandardDeliveryEnabled && isNoRushDeliveryEnabled;
};

export const canShowDeliverySpeedCrouton = ({
    storageData,
    showCrouton,
    slaOptions,
}: {
    storageData: DeliverySpeedAppStorage;
    showCrouton: boolean;
    slaOptions: DeliverySpeedDetail[];
}): boolean => {
    const isCroutonCountUnderLimit = isDeliverySpeedStorageValueValid(
        APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT,
        storageData,
    );

    return showCrouton && isCroutonCountUnderLimit && areBothStandardAndNorushEnabled(slaOptions);
};

export const getSelectedSlaFromSlaOptions = (
    slaOptions: DeliverySpeedDetail[],
    selectedOption: DELIVERY_SPEED,
): DeliverySpeedDetail | undefined => {
    return slaOptions?.find((option) => option.type === selectedOption);
};

export const checkServiceability = (
    statusCode: CART_STATUS_CODES | undefined,
    { userLoggedIn, selectedAddress }: ServiceabilityParams,
    targetStatus: CART_STATUS_CODES,
    deliveryValid: number,
) => statusCode === targetStatus && userLoggedIn && selectedAddress?.delivery_valid === deliveryValid;
